import { Dayjs } from 'dayjs';

import { SDate, SDuration, ServiceLocator } from '@summit/core';

import { assignSafe } from '@summit/core';
import { DayOfWeek } from '../../c-types/day-of-week.enum';
import { isNumber } from '../../object-functions';
import { IUserExperience } from '../../services/i-user-experience';
import { SummitUserExperienceService } from '../summit-user-experience.service';
import { DateRange } from './date-range';
import { DateRangeType, DateRangeTypeMapping } from './date-range-type.enum';

const FedExEndOfWeek = DayOfWeek.Friday;

export class Week {
  private _weekOffSet?: number;
  private _userCutOff?: Dayjs;
  start!: Dayjs;
  end!: Dayjs;
  fedExStart!: Dayjs;
  fedExEnd!: Dayjs;
  scheduleStart!: Dayjs;
  scheduleEnd!: Dayjs;
  ivmrStart!: Dayjs;
  ivmrEnd!: Dayjs;
  endOfWeek: DayOfWeek = DayOfWeek.Sunday;
  endOfScheduleWeek: DayOfWeek = DayOfWeek.Sunday;
  endOfIvmrWeek!: DayOfWeek;
  type = DateRangeType.TenantWeek;

  static GetWeeksBetween(startWeek: Week, endWeek: Week) {
    //console.log(`Getting Weeks between ${startWeek.start.format('MM/DD')}=>${endWeek.end.format('MM/DD')}`)
    const response = new Array<Week>();
    let currentWeek = new Week(startWeek);
    do {
      response.push(new Week(currentWeek));
      currentWeek = currentWeek.GetNextWeek();
      //console.log(`CurrentWeek's offset ${currentWeek.GetWeekOffset()}<=${endWeek.GetWeekOffset()}`)
    } while (currentWeek.GetWeekOffset() <= endWeek.GetWeekOffset());
    return response;
  }

  private static isDST(d: Dayjs): boolean {
    const jDate = d.toDate();
    const jan = new Date(d.year(), 0, 1).getTimezoneOffset();
    const jul = new Date(d.year(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== jDate.getTimezoneOffset();
  }

  private static AdjustForDST(inputDateTime: Dayjs): Dayjs {
    if (Week.isDST(inputDateTime)) {
      // console.log(`IS DST ${inputDateTime.format()}`)
      return inputDateTime.add(-1, 'hour');
    }
    return inputDateTime;
  }

  constructor(
    jsonData?: Partial<Week> | string | number | Dayjs,
    inputType: DateRangeType = DateRangeType.TenantWeek,
    endOfWeekOverride?: DayOfWeek
  ) {
    this.type = inputType ?? DateRangeType.TenantWeek;
    //* Configure week correctly
    if (endOfWeekOverride || endOfWeekOverride == 0) {
      this.endOfWeek = endOfWeekOverride;
      this.endOfScheduleWeek = endOfWeekOverride;
      this.endOfIvmrWeek = endOfWeekOverride;
    } else {
      //console.log("getting ux service")
      const userExperienceService = this.getUserExperienceService();
      //console.log("got ux service", userExperienceService)
      if (userExperienceService) {
        const currentUX = userExperienceService?.getUserExperience();
        //console.log('ux:', currentUX);
        this.endOfWeek = currentUX?.endOfWeek ?? this.endOfWeek;
        this.endOfScheduleWeek = currentUX?.endOfScheduleWeek ?? this.endOfScheduleWeek;
        //console.log(`Using EndOfWeek: ${DayOfWeekMapping[this.endOfWeek]} and End Of Schedule Week: ${DayOfWeekMapping[this.endOfScheduleWeek]}`, currentUX);
        this.endOfIvmrWeek = currentUX?.endOfIvmrWeek ?? this.endOfIvmrWeek ?? DayOfWeek.Saturday;
        // console.log(
        //   `Using EndOfIvmrWeek: ${DayOfWeekMapping[this.endOfIvmrWeek]} and End Of Schedule Week: ${
        //     DayOfWeekMapping[this.endOfIvmrWeek]
        //   }`,
        //   currentUX
        // );
      } else {
        // console.log(`Using Default EndOfWeek: ${DayOfWeekMapping[this.endOfWeek]}`);
      }
    }
    //console.log(`Using EndOfWeek: ${DayOfWeekMapping[this.endOfWeek]} and End Of Schedule Week: ${DayOfWeekMapping[this.endOfScheduleWeek]}`);
    //* Build Week
    if (SDate.isDayjs(jsonData)) {
      //console.log(`is SDate.new Lcl: ${jsonData.local().format()}, utc: ${jsonData.format()}`);
      this.Covering(jsonData, this.type);
    } else if (typeof jsonData === 'string') {
      // console.log('is string');
      if (jsonData.indexOf('/') >= 0) {
        this.Covering(SDate.newUtc(jsonData as string, 'MM/DD/YY'), this.type);
      } else {
        this.Covering(SDate.newUtc(jsonData as string), this.type);
      }
    } else if (isNumber(jsonData)) {
      //console.log(`week constructor received number = ${jsonData} with type ${DateRangeTypeMapping[inputType]}`);
      this.SetWeekOffset(jsonData, this.type);
    } else if (jsonData) {
      // console.log('is object');
      assignSafe(this, jsonData);
    }
    if (!this.start || !this.end) {
      // console.log(`week constructor defaulting to current ${DateRangeTypeMapping[inputType]}`);
      this.Covering(SDate.getNormalized(), this.type);
    }
    // console.log(`Week Constructor's final value ${this.start.format('MM/DD/YY')}-${this.end.format('MM/DD/YY')}`);
    this.endOfIvmrWeek ??= DayOfWeek.Saturday;
  }

  private getUserExperienceService<T extends IUserExperience>(): SummitUserExperienceService<T> {
    const userExperienceService = ServiceLocator?.injector?.get(SummitUserExperienceService<T>);
    return userExperienceService;
  }

  public ChangeOffsetBy(weekChange: number): Week {
    // console.log(`Changing Offset of ${this.start.format('M/D')}-${this.end.format('M/D')} by ${weekChange}`)
    const adjustedWeekOffset = weekChange;
    const shift = 7 * adjustedWeekOffset;
    this.start = SDate.GetStartOfDay(this.start.add(shift, 'day'));
    this.end = SDate.GetStartOfDay(this.end.add(shift, 'day'));
    this.fedExStart = SDate.GetStartOfDay(this.fedExStart.add(shift, 'day'));
    this.fedExEnd = SDate.GetStartOfDay(this.fedExEnd.add(shift, 'day'));
    this.scheduleStart = SDate.GetStartOfDay(this.scheduleStart.add(shift, 'day'));
    this.scheduleEnd = SDate.GetStartOfDay(this.scheduleEnd.add(shift, 'day'));
    this.ivmrStart = SDate.GetStartOfDay(this.ivmrStart.add(shift, 'day'));
    this.ivmrEnd = SDate.GetStartOfDay(this.ivmrEnd.add(shift, 'day'));
    this._weekOffSet = undefined;
    return this;
  }

  GetWeekOffset(): number {
    if (!this._weekOffSet) {
      const currentWeek = new Week(undefined, this.type, this.endOfWeek);
      const delta = this.end.diff(currentWeek.end, 'days') / 7;
      //console.log(`Calculating WeekOffset from ${this.end.format('M/D/Y')} to ${currentWeek.end.format('M/D/Y')} with delta of ${delta}`);
      // May need to test this to vary it by day of week
      this._weekOffSet = delta;
      // console.log(`Calculate WeekOffset of ${this._weekOffSet}`);
    }
    return this._weekOffSet;
  }

  public SetWeekOffset(relWeekOffSet: number, dateRangeType?: DateRangeType): Week {
    //console.log(`Creating based on week covering ${EboDate.getNormalized().format()} with offset ${weekOffSet}`)
    this.Covering(SDate.getNormalized(), dateRangeType).ChangeOffsetBy(relWeekOffSet);
    return this;
  }

  private Covering(targetDate: Dayjs, inputType: DateRangeType = DateRangeType.TenantWeek): Week {
    //console.log(`[Week.Covering]: Week type: ${inputType} Target Date = ${targetDate.format()}`);
    this.type = inputType;
    let weekDayForMath = 0;
    //HACK the add 1 hour then start of day is to handle the DST issue with the week calculation
    switch (this.type) {
      case DateRangeType.TenantWeek:
      case DateRangeType.ReportWeek:
        this.end = SDate.GetStartOfDay(this.CalculateCurrentTenantWeekend(targetDate));
        this.start = SDate.GetStartOfWeekUtc(this.end);
        //console.log(`Week.Covering TenantWeek: ${this.start.format()} - ${this.end.format()}`);
        weekDayForMath = this.endOfWeek == 0 ? 7 : this.endOfWeek;
        this.fedExEnd = SDate.GetStartOfDay(this.end.add(-1 * (weekDayForMath - FedExEndOfWeek), 'days'));
        this.fedExStart = SDate.GetStartOfWeekUtc(this.fedExEnd);
        //console.log(`Week.Covering TenantWeek: Fedex ${this.fedExStart.format()} - ${this.fedExEnd.format()}`);
        break;
      case DateRangeType.SettlementWeek:
        this.fedExEnd = SDate.GetStartOfDay(this.CalculateCurrentFedExWeekend(targetDate));
        this.fedExStart = SDate.GetStartOfWeekUtc(this.fedExEnd);
        //console.log(`Week.Covering Settlement: ${this.fedExStart.format()} - ${this.fedExEnd.format()}`);
        weekDayForMath = this.endOfWeek == 0 ? 7 : this.endOfWeek;
        //console.log(`weekDayForMath = ${weekDayForMath}`);
        this.end = SDate.GetStartOfDay(this.fedExEnd.add(weekDayForMath - FedExEndOfWeek, 'days'));
        this.start = SDate.GetStartOfWeekUtc(this.end);
        break;
      case DateRangeType.IVMRWeek:
        this.end = SDate.GetStartOfDay(this.CalculateCurrentTenantWeekendFromIvmrDate(targetDate));
        this.start = SDate.GetStartOfWeekUtc(this.end);
        weekDayForMath = this.endOfWeek == 0 ? 7 : this.endOfWeek;
        this.fedExEnd = SDate.GetStartOfDay(this.end.add(-1 * (weekDayForMath - FedExEndOfWeek), 'days'));
        this.fedExStart = SDate.GetStartOfWeekUtc(this.fedExEnd);
        break;
      case DateRangeType.ScheduleWeek:
        this.end = SDate.GetStartOfDay(this.CalculateCurrentTenantWeekendFromScheduleDate(targetDate));
        this.end = this.end.add(1, 'hour').startOf('day');
        this.start = SDate.GetStartOfWeekUtc(this.end);
        weekDayForMath = this.endOfWeek == 0 ? 7 : this.endOfWeek;
        this.fedExEnd = SDate.GetStartOfDay(this.end.add(-1 * (weekDayForMath - FedExEndOfWeek), 'days'));
        this.fedExStart = SDate.GetStartOfWeekUtc(this.fedExEnd);
        break;
      default:
        throw new Error(`Week.Covering does not support Week Type of ${DateRangeTypeMapping[this.type]}`);
    }

    const scheduleOffSet =
      (-1 * (weekDayForMath - (this.endOfScheduleWeek == DayOfWeek.Sunday ? 7 : this.endOfScheduleWeek))) % 7;
    //console.log(`Schedule Offset = ${scheduleOffSet} for ${this.endOfWeek} to ${this.endOfScheduleWeek}`);
    this.scheduleStart = SDate.GetStartOfDay(this.start.add(scheduleOffSet, 'days'));
    this.scheduleEnd = SDate.GetStartOfDay(this.end.add(scheduleOffSet, 'days'));
    const ivmrOffset = (-1 * (weekDayForMath - (this.endOfIvmrWeek == DayOfWeek.Sunday ? 7 : this.endOfIvmrWeek))) % 7;
    //console.log(`IVMR Offset = ${ivmrOffset} for ${this.endOfWeek} to ${this.endOfIvmrWeek}`);
    this.ivmrStart = SDate.GetStartOfDay(this.start.add(ivmrOffset, 'days'));
    this.ivmrEnd = SDate.GetStartOfDay(this.end.add(ivmrOffset, 'days'));
    this._weekOffSet = undefined;
    //console.log(`Week covering ${targetDate.format()} : Type ${inputType} starts ${this.ToLongString()}`);
    return this;
  }

  //Copied Server
  private CalculateCurrentTenantWeekend(inputDateTime?: Dayjs): Dayjs {
    const selDate = SDate.newUtc(inputDateTime) ?? SDate.getNormalized();
    //console.log(`[Week.CalculateCurrentTenantWeekend]: Input Date = ${selDate.format()}`);
    let dayShift = 0;
    dayShift = (7 + (selDate.day() - this.endOfWeek - 1)) % 7;
    const shiftedDate = selDate.add(-1 * dayShift + 6, 'days');
    return shiftedDate.startOf('day'); //.utc().startOf('day');
  }

  private CalculateCurrentTenantWeekendFromScheduleDate(inputDateTime?: Dayjs): Dayjs {
    const weekDayForMath = this.endOfWeek == DayOfWeek.Sunday ? 7 : this.endOfWeek;
    const scheduleOffSet =
      (-1 * (weekDayForMath - (this.endOfScheduleWeek == DayOfWeek.Sunday ? 7 : this.endOfScheduleWeek))) % 7;
    //console.log(`DayShift for Schedule=> ${scheduleOffSet}`)
    const selDate = SDate.newUtc(inputDateTime) ?? SDate.getNormalized();
    const shiftedDate = SDate.GetStartOfDay(selDate.add(-1 * scheduleOffSet, 'days'));
    return this.CalculateCurrentTenantWeekend(shiftedDate.startOf('day'));
  }

  private CalculateCurrentTenantWeekendFromIvmrDate(inputDateTime?: Dayjs): Dayjs {
    const weekDayForMath = this.endOfWeek == DayOfWeek.Sunday ? 7 : this.endOfWeek;
    const ivmrOffset = (-1 * (weekDayForMath - (this.endOfIvmrWeek == DayOfWeek.Sunday ? 7 : this.endOfIvmrWeek))) % 7;
    //console.log(`DayShift for Schedule=> ${scheduleOffSet}`)
    const selDate = SDate.newUtc(inputDateTime) ?? SDate.getNormalized();
    const shiftedDate = SDate.GetStartOfDay(selDate.add(-1 * ivmrOffset, 'days'));
    return this.CalculateCurrentTenantWeekend(shiftedDate.startOf('day'));
  }

  private CalculateCurrentFedExWeekend(inputDateTime?: Dayjs): Dayjs {
    const selDate: Dayjs = inputDateTime ?? SDate.getNormalized();
    let dayShift = 0;
    dayShift = (7 + (selDate.day() - FedExEndOfWeek - 1)) % 7;
    const shiftedDate = SDate.GetStartOfDay(selDate.add(-1 * dayShift + 6, 'days'));
    //console.log(`FedEx Weekend ${shiftedDate.format()}`);
    return shiftedDate;
  }

  isEditable(ux: IUserExperience): boolean {
    //console.log('testing is editable');
    const debug = false;
    if (debug) console.log(`is editable:`, this);
    if (ux?.isAdmin()) return true;
    if (this._weekOffSet === undefined) {
      this.GetWeekOffset();
    }
    if (this._weekOffSet === undefined) return false;
    if (debug) console.log(`Testing Week Offset of ${this._weekOffSet}`);
    if (this._weekOffSet < -1) {
      if (debug) console.log(`is NOT editable because week offset ${this._weekOffSet} < -1`);
      return false;
    }
    if (this._weekOffSet > -1) {
      if (debug) console.log(`is NOT editable because week offset ${this._weekOffSet} < -1`);
      return true;
    }
    if (this._weekOffSet === -1 && SDate.newUtc().isBefore(this.CalculateUserCutOff(ux))) {
      if (debug)
        console.log(
          `is editable because week offset ${
            this._weekOffSet
          } = -1 and we (user) are (${SDate.newUtc().toISOString()}) before ${this.CalculateUserCutOff(
            ux
          ).toISOString()}`
        );
      return true;
    }
    if (ux?.isManager() && (ux.overrideReconcileLock || SDate.newUtc().isBefore(this.CalculateManagerCutOff()))) {
      if (debug) console.log(`is editable because week we are a manager and override lock is enabled`);
      return true;
    }
    if (ux?.isBackOffice()) {
      if (debug) console.log(`is editable because week we are a manager and override lock is enabled`);
      return true;
    }
    if (ux?.isPayroll() && this._weekOffSet >= -1) {
      if (debug) console.log(`is editable because week we are a payroll and last week or later`);
      return true;
    }

    if (debug) console.log(`is NOT editable because week offset ${this._weekOffSet} = -1 and no other rules to test`);
    return false;
  }

  IsReconciled(): boolean {
    if (this._weekOffSet === undefined) {
      this.GetWeekOffset();
    }
    //console.log(`Testing Week Offset of ${this._weekOffSet} for Reconcile`);
    if (this._weekOffSet !== undefined && this._weekOffSet < -1) {
      return true;
    }
    if (this._weekOffSet !== undefined && this._weekOffSet >= 0) {
      return false;
    }
    if (this._weekOffSet === -1 && SDate.newUtc().isBefore(this.CalculateFedExCutOff())) {
      //console.log(`Testing Week Offset of ${this._weekOffSet} for Reconcile - returning false`);
      return false;
    }
    //console.log(`Testing Week Offset of ${this._weekOffSet} for Reconcile - returning True`);
    return true;
  }

  private CalculateManagerCutOff(): Dayjs {
    let cutOff: Dayjs;
    if (this.endOfWeek == DayOfWeek.Friday) {
      cutOff = SDate.newUtc(this.fedExEnd)
        .add(3, 'days')
        .add(7 + 12, 'hours'); // Noon PST
      cutOff = Week.AdjustForDST(cutOff);
    } else {
      cutOff = this.CalculateFedExCutOff();
    }
    // console.log(`Manager CutOff ${cutOff.utcOffset(-8).format('M/D/Y hh:mm:ss A Z')} for FedEx Week ending ${this.fedExEnd.format('M/D/Y')}`);
    return cutOff;
  }

  private CalculateUserCutOff(ux: IUserExperience): Dayjs {
    if (this._userCutOff) return this._userCutOff;
    const weekCutOff: Dayjs = this.GetFedExCutOff();
    let cutOff = SDate.new(weekCutOff);
    cutOff = Week.AdjustForDST(cutOff);
    /* pre solution */
    /*
    if (this.endOfWeek == DayOfWeek.Friday) {
      cutOff = SDate.newUtc(this.fedExEnd).add(3, 'days').add(8, 'hours'); //Standard Time
    } else {
      cutOff = this.CalculateFedExCutOff();
    }
    */
    /* To be tested 4/16/22 then rolled out */
    if (ux?.isBackOffice()) {
      this._userCutOff = weekCutOff;
      return this._userCutOff;
    }
    //cutOff.hours(0);
    let cutOffDayOfWeek: DayOfWeek = DayOfWeek.Tuesday;
    let cutOffTime = SDuration.new({ hours: 21 }, true);
    const lclRunParameters = ux?.divisionConfig?.employeeParameters?.digitalLogParameters ?? null;
    if (lclRunParameters == null) return weekCutOff;
    //console.log('display parameters:', lclDivisionConfig, ux);
    if (ux?.isManagerOnly()) {
      cutOffDayOfWeek = lclRunParameters.managerCutOffDay;
      cutOffTime = lclRunParameters.managerCutOffTime;
      //console.log(`User is Manager: Using ${DayOfWeekMapping[cutOffDayOfWeek]} : ${cutOffTime.hours()}:${cutOffTime.minutes()}`);
    } else {
      cutOffDayOfWeek = lclRunParameters.driverCutOffDay;
      cutOffTime = lclRunParameters.driverCutOffTime;
      //console.log(`User is Driver: Using ${DayOfWeekMapping[cutOffDayOfWeek]} : ${cutOffTime.hours()}:${cutOffTime.minutes()}`);
    }
    while (cutOff.day() != cutOffDayOfWeek) {
      cutOff = cutOff.add(-1, 'day');
    }
    //console.log(`Using ${cutOff.format('M/D/YY HH:mm')}`);
    //Setting to Eastern Time Zone
    cutOff = cutOff.utcOffset(-4).add(cutOffTime);
    cutOff = Week.AdjustForDST(cutOff);
    cutOff = cutOff > weekCutOff ? weekCutOff : cutOff;
    //console.log(`Calculated Cut Off: ${cutOff.format('M/D/YY HH:mm')}`);
    //Default to driver rules
    //console.log(`current timeOffset = ${cutOff.utcOffset()}`);
    //console.log(`User CutOff ${cutOff.format('M/D/Y hh:mm:ss A Z')} for FedEx Week ending ${this.fedExEnd.format('M/D/Y')}` );
    this._userCutOff = cutOff;
    return this._userCutOff;
  }

  private CalculateFedExCutOff(): Dayjs {
    let cutOff = SDate.newUtc(this.fedExEnd).add(4, 'days').add(26, 'hours'); //Standard Time
    cutOff = Week.AdjustForDST(cutOff);
    //console.log(`Cutoff is ${cutOff.local().toLocaleString()} at ${SDate.new().toLocaleString()}`)
    // console.log(`FedEx CutOff ${cutOff.local().format('M/D/Y hh:mm:ss A')} for FedEx Week ending ${this.fedExEnd.format('M/D/Y')}`);
    return cutOff;
  }

  GetFedExCutOff(): Dayjs {
    return this.CalculateFedExCutOff();
  }

  ToDateRange(dateRangeType: DateRangeType): DateRange {
    let response: DateRange;
    switch (dateRangeType) {
      case DateRangeType.SettlementWeek:
        response = new DateRange({
          begin: this.fedExStart,
          end: this.fedExEnd
        });
        break;
      case DateRangeType.ScheduleWeek:
        response = new DateRange({
          begin: this.scheduleStart,
          end: this.scheduleEnd
        });
        break;
      case DateRangeType.IVMRWeek:
        response = new DateRange({
          begin: this.ivmrStart,
          end: this.ivmrEnd
        });
        break;
      default:
        response = new DateRange({
          begin: this.start,
          end: this.end
        });
    }
    // console.log(
    //   `[Week.ToDateRange]: Date Range Type: ${dateRangeType}`,
    //   response.begin.format(),
    //   response.end.format()
    // );
    return response;
  }

  ToLongString(): string {
    return `Week of ${this.start.format('MM/DD/YY')}-${this.end.format('MM/DD/YY')} (Sch. ${this.scheduleStart.format(
      'MM/DD/YY'
    )}-${this.scheduleEnd.format('MM/DD/YY')}) matching FedEx week of ${this.fedExStart.format(
      'MM/DD/YY'
    )}-${this.fedExEnd.format('MM/DD/YY')}`;
  }

  IsDateInCarryForward(eventDate: Dayjs): boolean {
    const response = eventDate.isBetween(this.fedExEnd, this.end.add(1, 'day'));
    // console.log(`Checking ${eventDate.format()} between ${this.fedExEnd.format()} && ${this.end.add(1, 'day').format()} = ${response}`)
    return response;
  }

  GetPriorWeek(): Week {
    const response = new Week(this.GetWeekOffset() - 1);
    //console.log(`Current Week ${this.start.format()}-${this.end.format()}, Prior Week ${response.start.format()}-${response.end.format()}`);
    return response;
  }

  GetNextWeek(): Week {
    const response = new Week(this.GetWeekOffset() + 1);
    //console.log(`Current Week ${this.start.format()}-${this.end.format()}, Next Week ${response.start.format()}-${response.end.format()}`   );
    return response;
  }

  Contains(date: Dayjs): boolean {
    //console.log(`Checking ${date.format()} between ${this.start.format()} && ${this.end.format()}`);
    return date.isSameOrAfter(this.start) && date.isSameOrBefore(this.end);
  }
  ContainsSettlement(date: Dayjs): boolean {
    // console.log(
    //   `Checking Settlement: ${date.format()} between ${this.fedExStart.format()} && ${this.fedExEnd.format()}`
    // );
    return date.isSameOrAfter(this.fedExStart) && date.isSameOrBefore(this.fedExEnd);
  }
}
